import {
  Button,
  Checkbox,
  Form,
  Link,
  OptionsValues,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { getRegional } from "library/getRegional";
import { mapValueToLabel } from "library/utils";
import { mapDurationOfHardshipToEventLabel } from "pages/HardshipApplication/Prescreening/constants";
import {
  REGION_APPLICATION_URL_MAP,
  REGION_ON_HARDSHIP_LABEL_MAP,
} from "pages/HardshipApplication/routes/constants";
import { ChangeEvent } from "react";
import { useEventTracking, usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import {
  applyingOnBehalfOfOtherOption,
  durationOfHardshipOptions,
  trueFalseOptions,
} from "../options";
import { ActionTypes, PrescreeningState } from "../reducer/types";

const PrescreeningPage = () => {
  const { state, dispatch } = useHardshipApplicationContext();

  const { goBack, navigate } = useNav();

  const { methods, Field } = useBasisForm<PrescreeningState>({
    defaultValues: state.prescreening,
  });

  const { trackEvent } = useEventTracking();

  const regionCode = getRegional("code");

  const { watch } = methods;

  const onSubmit = (values: PrescreeningState) => {
    dispatch({ type: ActionTypes.COMPLETE_PRESCREENING, ...values });
    navigate("personal-details");
  };

  const applyingOnBehalfOfOther = watch("applyingOnBehalfOfOther");

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8" width="100%" width-sm="640">
            <Text as="h2" textStyle="heading5">
              Personalise your Hardship Care application
            </Text>
            <Text>
              By answering these questions, you’ll help us personalise your
              application so it’s quicker and easier for you to complete.
            </Text>
            <Stack gap="8" width="100%" width-sm="452">
              <Field
                name="applyingOnBehalfOfOther"
                label="Are you applying for Hardship Care for another person?"
                options={applyingOnBehalfOfOtherOption}
                columns={2}
                testId="iConfirm"
                as={RadioGroup}
              />
              <Field
                name="onHardshipInLast24Months"
                label={REGION_ON_HARDSHIP_LABEL_MAP[regionCode]}
                options={trueFalseOptions}
                columns={2}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value as OptionsValues<
                    typeof trueFalseOptions
                  >;
                  trackEvent({
                    event: {
                      category: "radio-button",
                      action: "select",
                      location: "last24mth",
                      label: mapValueToLabel(value, trueFalseOptions),
                    },
                  });
                }}
                as={RadioGroup}
              />
            </Stack>
            <Field
              name="durationOfHardship"
              label="How long will you need Hardship Care for?"
              options={durationOfHardshipOptions}
              columns={2}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target;
                trackEvent({
                  event: {
                    category: "radio-button",
                    action: "select",
                    location: "duration",
                    label: mapDurationOfHardshipToEventLabel[value],
                  },
                });
              }}
              as={RadioGroup}
            />
            {applyingOnBehalfOfOther === "yes_on_behalf" && (
              <Text>
                You need to know that under the Privacy Act, we can only contact
                a person who has been authorised to act for the account holder.
                If you are not authorised, the account holder will need to
                complete an{" "}
                <Link
                  href={REGION_APPLICATION_URL_MAP[regionCode]}
                  newTab={false}
                >
                  Authorised Third-Party form
                </Link>{" "}
                and send it back to us. Otherwise, you won’t be able to apply
                for Hardship Care on their behalf.
              </Text>
            )}
            {applyingOnBehalfOfOther === "yes_on_behalf" && (
              <Field
                name="authorizedThirdPartyForm"
                label="I can confirm the account holder has submitted an Authorized Third Party form"
                hideLabel
                as={Checkbox}
              >
                I can confirm the account holder has submitted an Authorized
                Third Party form
              </Field>
            )}
            <FormActions>
              <FormActions.Primary width="260">
                <Button variant="primary" type="submit">
                  Start your application
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary width="160">
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};

export const Prescreening = () => {
  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "screening",
      pageTitle: "Personalise your Hardship Care application",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  return <PrescreeningPage />;
};
